'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface RetailerFaviconProps {
  domain: string | null;
  displayName: string;
  /** Shown when no source yields a real mark. */
  fallback?: ReactNode;
  className?: string;
  /**
   * Whether this mark is on a surface the reader can actually see.
   *
   * The offers panel is in the SSR markup of every product card but hidden
   * inside the collapsed pill, so fetching five retailers' icons per card on
   * load would be pure waste. It passes `false` until the panel is first
   * opened. Defaults to true for the always-visible CTA marks.
   */
  active?: boolean;
}

type IconSource = {
  url: (domain: string) => string;
  /**
   * Smallest edge, in pixels, an answer must have to count as a real mark.
   * It is above 1 only for the sources that say "I have nothing" with a
   * PICTURE instead of an error — for those, size is the only tell, because
   * `onError` never fires on an HTTP 200.
   */
  minPx: number;
};

/**
 * Sources, in order. Each is the retailer's OWN icon — we neither host nor
 * redraw anyone's mark, we point at the one their site publishes, which is what
 * every comparison surface does to say "this link goes to that store".
 *
 *  1. Google's s2 proxy. Cached, fast, CORS-clean, and right for 37 of the 38
 *     merchants in `offer-merchants.json`. Its "nothing" answer is a 16×16
 *     globe on an HTTP 404, hence `minPx: 17`.
 *  2. The retailer's own /favicon.ico, for the ones Google's crawler missed.
 *     Trusted at ANY size: walmart.ca's real icon IS 16×16, and rejecting it
 *     would trade a real mark for a letter.
 *  3. Yandex's favicon proxy, for the ones BOTH of those miss. That is one
 *     merchant today and it is a category, not an accident: mec.ca sits behind
 *     a Cloudflare challenge that answers every unauthenticated request —
 *     `/favicon.ico` and `/robots.txt` alike — with a 403 HTML interstitial, so
 *     its origin can never serve us an image, and Google has no cached copy.
 *     Yandex does. Its "nothing" answer is a 1×1 PNG on an HTTP 200, hence
 *     `minPx: 2`; it returns that for darty/screwfix/walmart.ca, all of which
 *     are long since resolved by sources 1-2 and never reach it.
 *
 * Order matters: Google survives hotlink protection, the origin is the one that
 * has an icon at all when Google missed the site, and only then do we send the
 * reader's browser to a third proxy. Measured across the whole registry, not
 * assumed — with all three, nothing falls through to the monogram.
 *
 * DuckDuckGo (`icons.duckduckgo.com/ip3/`) was measured and REJECTED for this
 * slot: its miss is a generic globe byte-identical to a bogus domain's, i.e.
 * undetectable, so it would put a globe on a retailer instead of its letter.
 */
const SOURCES: IconSource[] = [
  {
    url: (domain) => `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`,
    minPx: 17,
  },
  { url: (domain) => `https://${domain}/favicon.ico`, minPx: 1 },
  {
    url: (domain) => `https://favicon.yandex.net/favicon/v2/${encodeURIComponent(domain)}?size=120`,
    minPx: 2,
  },
];

/** A retailer's mark, with a monogram for the ones that have none. */
export function RetailerFavicon({
  domain,
  displayName,
  fallback,
  className = 'h-[1.15rem] w-[1.15rem]',
  active = true,
}: RetailerFaviconProps) {
  // Which source we are currently trying, and whether its image came back as a
  // real mark. `loaded` stays false through every retry, so the monogram is on
  // screen the whole time rather than flashing in and out.
  const [sourceIndex, setSourceIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const initial = (displayName.trim().charAt(0) || '?').toUpperCase();
  const src = domain && sourceIndex < SOURCES.length ? SOURCES[sourceIndex].url(domain) : null;

  const nextSource = useCallback(() => setSourceIndex((current) => current + 1), []);

  /** Accept the mark, or move to the next source. */
  const judge = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      // Each source declares how small an answer may be before it is one of
      // that source's stand-in pictures rather than a retailer's mark.
      const minPx = SOURCES[sourceIndex]?.minPx ?? 1;
      if (Math.min(naturalWidth, naturalHeight) < minPx) nextSource();
      else setLoaded(true);
    },
    [nextSource, sourceIndex],
  );

  // A CACHED icon finishes before React attaches `onLoad` — which is the normal
  // path on every page after the first — and that event then never fires, so
  // `loaded` stayed false and a perfectly good logo sat there `display: none`
  // behind its own monogram. Re-run the same judgement against whatever the
  // element already holds once it is mounted.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !img.complete) return;
    if (img.naturalWidth === 0) nextSource();
    else judge(img.naturalWidth, img.naturalHeight);
  }, [src, judge, nextSource]);

  const fallbackNode = fallback ?? (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-sm bg-black/10 text-[0.65rem] font-semibold leading-none text-black/75 ${className}`}
      aria-hidden
    >
      {initial}
    </span>
  );

  // `active` gates the REQUEST, and that gate has to be mount/unmount rather
  // than a class: this element is `display: none` until a source proves good,
  // and a `display: none` image with `loading="lazy"` is never fetched at all —
  // no intersection, no load event, no icon, forever. That is why every
  // retailer in this panel showed a monogram before 2026-08 no matter how good
  // its favicon was. Hence: no `loading` attribute here, and nothing rendered
  // until the surface is real.
  if (!src || !active) return fallbackNode;

  return (
    <>
      <img
        // Keyed by source so switching URLs remounts the element: without it a
        // browser that has already fired onError for this <img> may not fire
        // load/error again for the replacement src.
        key={src}
        ref={imgRef}
        src={src}
        alt=""
        aria-hidden
        className={`shrink-0 object-contain ${className} ${loaded ? '' : 'hidden'}`}
        decoding="async"
        referrerPolicy="no-referrer"
        onError={nextSource}
        onLoad={(event) => judge(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
      />
      {!loaded && fallbackNode}
    </>
  );
}
